import { useState, useEffect } from "react";
import { Phone } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope } from 'react-icons/fa';
import { Link } from 'react-router-dom';
// OptimizedImage component with mobile fix
const OptimizedImage = ({ src, alt, className, style, isMobile }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const placeholderColor = "#1e293b";

  useEffect(() => {
    const img = new Image();
    img.src = src;
    if (img.complete) setImageLoaded(true);
    else img.onload = () => setImageLoaded(true);
    return () => { img.onload = null; };
  }, [src]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className={`absolute inset-0 ${imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-700`}
        style={{ backgroundColor: placeholderColor }}
      />
      <img
        src={src}
        srcSet={`
          ${src.replace('.jpg', '-mobile.jpg')} 480w,
          ${src} 1200w
        `}
        sizes="(max-width: 767px) 480px, 1200px"
        alt={alt}
        loading="eager"
        fetchPriority="high"
        className={`${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-700 ${
          isMobile ? 'w-full h-auto' : 'w-full h-full object-cover'
        }`}
        style={{
          ...style,
          ...(isMobile && { minHeight: '100%' })
        }}
      />
    </div>
  );
};

// Modern ContactButton with floating effect
const ContactButton = ({ href, icon: Icon, label, external = false }) => (
  <a 
    href={href}
    {...(external && { target: "_blank", rel: "noopener noreferrer" })}
    className="group relative bg-white/15 backdrop-blur-lg text-white 
               px-5 py-5 rounded-3xl font-medium shadow-lg shadow-black/20
               hover:bg-white/25 hover:shadow-xl hover:shadow-black/30 
               transition-all duration-500 flex items-center justify-center
               transform hover:scale-110 hover:-translate-y-2
               border border-white/30 hover:border-white/50
               min-w-[65px] md:min-w-[75px]"
    title={label}
  >
    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white group-hover:scale-125 transition-all duration-500" />
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    <div className="absolute inset-0 rounded-3xl border-2 border-white/20 scale-100 group-hover:scale-125 group-hover:opacity-0 transition-all duration-500"></div>
  </a>
);

// Floating particles background


const HeaderLayout = ({ topicData }) => {
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    const imagePath = topicData?.mainImage || '/אראלחןיושביםראשי.jpg';
    setBackgroundImage(imagePath);

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = imagePath;
    document.head.appendChild(preloadLink);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
      if (preloadLink.parentNode) {
        preloadLink.remove();
      }
    };
  }, [topicData?.mainImage]);

  if (!backgroundImage) {
    return (
      <div className="relative h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-400/30 border-t-blue-400"></div>
          <div className="absolute inset-2 animate-pulse rounded-full h-16 w-16 border-4 border-transparent border-b-blue-300/50"></div>
          <div className="absolute inset-4 animate-spin rounded-full h-12 w-12 border-2 border-transparent border-r-white/60" style={{ animationDirection: 'reverse' }}></div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="relative h-auto overflow-hidden bg-slate-900 ">
        {/* תמונה עם הפתרון שעבד בעמוד הקשר */}
        <div className="relative w-full min-h-[40vh] overflow-hidden">
          <OptimizedImage 
            src={backgroundImage}
            alt={topicData?.name || 'תמונת רקע'}
            className="w-full h-auto"
            style={{ 
              objectPosition: 'center center',
              minHeight: '100%'
            }}
            isMobile={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
        </div>
        
        {/* תוכן בחלק התחתון */}
        <div className="relative bg-gradient-to-b from-black/80 to-white px-4 py-10">
          <div className="text-white space-y-4 text-center">
            <h1 className="text-3xl font-bold">משרד עורכי דין</h1>
            <div className="flex items-center justify-center space-x-4 space-x-reverse">
              <div className="h-px bg-gradient-to-r from-blue-400 to-transparent flex-1 max-w-16"></div>
              <h2 className="text-lg font-light text-blue-200">מיכלסון דותן ושות</h2>
              <div className="h-px bg-gradient-to-l from-blue-400 to-transparent flex-1 max-w-16"></div>
            </div>
            <p className="text-sm bg-black/30 backdrop-blur-sm rounded-xl px-4 py-2 inline-block">
              מתמחים ב{topicData?.name || "משפט"}
            </p>
            
            <div className="flex justify-center gap-3 pt-2">
              <ContactButton href="tel:0532788686" icon={Phone} label="טלפון" />
              <ContactButton href="https://wa.me/972532788686" icon={FaWhatsapp} label="WhatsApp" external />
              <ContactButton href="mailto:office@mdc-lawfirm.com" icon={FaEnvelope} label="אימייל" external />
              <ContactButton href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" icon={FaWaze} label="Waze" external />
            </div>
          </div>
        </div>

       
      </div>
    );
  }

  // Desktop & Tablet Design
  return (
 <div className="relative min-h-screen overflow-hidden bg-slate-900">

      {/* Full screen background */}
      <div className="absolute inset-0">
        <OptimizedImage 
          src={backgroundImage}
          alt={topicData?.name || 'תמונת רקע'}
          className="w-full h-full object-cover"
          style={{ objectPosition: isTablet ? 'center 40%' : 'center 30%' }}
          isMobile={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40"></div>
      </div>

  

      {/* Content positioned asymmetrically */}
      <div className="relative h-full flex items-center z-10">
        <div className="w-full max-w-7xl mx-auto px-8 lg:px-16">
          <div className="grid lg:grid-cols-3 gap-12 items-center">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-6">
                <div className="space-y-4">
                  <h1 className="text-5xl xl:text-7xl font-bold text-white leading-tight">
                    משרד עורכי דין
                  </h1>
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <div className="h-px bg-gradient-to-r from-blue-400 to-transparent flex-1"></div>
                    <h2 className="text-2xl xl:text-3xl font-light text-blue-200">
                      מיכלסון דותן ושות
                    </h2>
                    <div className="h-px bg-gradient-to-l from-blue-400 to-transparent flex-1"></div>
                  </div>
                </div>
                <p className="text-xl xl:text-2xl text-white/90 font-medium bg-black/30 backdrop-blur-sm rounded-2xl px-6 py-4 inline-block">
                  מתמחים ב{topicData?.name || "משפט"}
                </p>
              </div>
              <div className="flex flex-wrap gap-6 pt-8">
                <ContactButton href="tel:0532788686" icon={Phone} label="טלפון" />
                <ContactButton href="https://wa.me/972532788686" icon={FaWhatsapp} label="WhatsApp" external />
                <ContactButton href="mailto:office@mdc-lawfirm.com" icon={FaEnvelope} label="אימייל" external />
                <ContactButton href="https://waze.com/ul?ll=31.9850714,34.7701406&navigate=yes" icon={FaWaze} label="Waze" external />
              </div>
            </div>
            {/* Decorative side card */}
            <div className="hidden lg:block">
            <Link to="/contact" className="block">
              <div className="bg-white/10 rounded-3xl p-8 shadow-2xl shadow-black/30 border border-white/20 transform rotate-3 hover:rotate-0 transition-transform duration-700">
                <div className="space-y-4 text-center">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                    <Phone className="w-8 h-8 text-blue-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">ייעוץ מקצועי</h3>
                  <p className="text-sm text-white/80">
                    קבלו ייעוץ ראשוני ללא התחייבות
                  </p>
                </div>
              </div>
            </Link>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderLayout;